import {
	Box,
	Button,
	DialogContent,
	FormControlLabel,
	Grid,
	MenuItem,
	Radio,
	RadioGroup,
	makeStyles,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { verifyError } from "Components/Common/helpers/verifyError";
import { useCompany } from "Components/Company/hooks/useCompany";
import { getValidMembers } from "Components/Members/MembersHelper";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { useModalController } from "Components/Modal/Modal";
import ModalActions from "Components/Modal/ModalActions";
import ModalTitle from "Components/Modal/ModalTitle";
import TinyModal from "Components/Modal/TinyModal";
import { useIsProjectRedirectable } from "Components/Project/ProjectHelper";
import { useProjects } from "Components/Project/hooks/useProjects";
import {
	getScheduleEntryTypeIcon,
	useTranslateScheduleEntryType,
} from "Components/ScheduleEntryType/ScheduleEntryTypeHelper";
import { useScheduleEntryTypes } from "Components/ScheduleEntryType/hooks/useScheduleEntryTypes";
import { useScheduleResources } from "Components/ScheduleResource/hooks/useScheduleResources";
import { px } from "Configs/theme";
import DatePickerField from "Fields/DatePickerField";
import ProjectField from "Fields/ProjectField";
import SelectField from "Fields/SelectField";
import TextField from "Fields/TextField";
import TimePickerField from "Fields/TimePickerField";
import { getColorHex } from "Helpers/ColorHelper";
import { useIsBrowserOrIpadOs } from "Helpers/PlatformHelper";
import { useRedirectToProject } from "Helpers/RedirectHelper";
import { useTranslation } from "Providers/TranslationProvider";
import { ScheduleEntry, ScheduleEntryTypeCategory } from "domain-entities";
import { useSnackbar } from "material-ui-snackbar-provider";
import moment from "moment";
import React, { FC, Fragment, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { ScheduleEntryKind } from "./ScheduleEntryHelper";
import { useScheduleEntries } from "./hooks/useScheduleEntries";
import { useVisibleMinutes } from "Components/WorkingHours/hooks/useVisibleMinutes";
import { getMinutesFromMoment } from "Components/WorkingHours/helpers/getMinutesFromMoment";

interface Props {
	scheduleEntry: ScheduleEntry;
	close: () => void;
	disableEdit: () => void;
	isNew?: boolean;
}

interface FormValues {
	startDate: moment.Moment;
	startTime: moment.Moment;
	endDate: moment.Moment;
	endTime: moment.Moment;
	userId: string;
	resourceId: string;
	kind: ScheduleEntryKind;
	typeId: string;
	projectId: string;
	projectNoteContent: string;
}

const ScheduleEntryForm: FC<Props> = (props) => {
	const { t } = useTranslation();
	const isInternalAdmin = useIsInternalAdmin();
	const companyMembers = useCompany((state) => state.item?.members);
	const [isDeleting, setIsDeleting] = useState(false);
	const translateScheduleEntryType = useTranslateScheduleEntryType();
	const classes = useStyles();
	const scheduleEntries = useScheduleEntries((state) => state.items);
	const scheduleResources = useScheduleResources((state) => state.items);
	const visibleMinutes = useVisibleMinutes();
	const snackbar = useSnackbar();
	const projects = useProjects((state) => state.projects);
	const isBrowserOrIpadOs = useIsBrowserOrIpadOs();
	const changeVisibleHoursMC = useModalController<FormValues>();
	const isDeleteButtonVisible = isInternalAdmin && !props.isNew;
	const scheduleEntryTypeCategory = props.scheduleEntry.resourceId
		? ScheduleEntryTypeCategory.RESOURCE
		: ScheduleEntryTypeCategory.USER;

	const scheduleEntryTypes = useScheduleEntryTypes((state) => state.items);

	const defaultValues: FormValues = {
		startDate: moment.unix(props.scheduleEntry.startTime).startOf("day"),
		startTime: moment.unix(props.scheduleEntry.startTime),
		endDate: moment.unix(props.scheduleEntry.endTime).startOf("day"),
		endTime: moment.unix(props.scheduleEntry.endTime),
		userId: props.scheduleEntry.userId || "",
		resourceId: props.scheduleEntry.resourceId || "",
		kind: props.scheduleEntry.typeId
			? ScheduleEntryKind.TYPE
			: ScheduleEntryKind.PROJECT,
		typeId: props.scheduleEntry.typeId || "",
		projectId: props.scheduleEntry.projectId || "",
		projectNoteContent: props.scheduleEntry.projectId
			? projects.find((project) => project.id === props.scheduleEntry.projectId)
					?.noteContent || ""
			: "",
	};

	const validationSchema = yup.object().shape({
		startTime: yup
			.object<moment.Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_StartTime"),
				})
			)
			.test(
				"test-StartTime",
				t("Common_Form_Error_InvalidTime"),
				function (value) {
					return moment(value).isValid();
				}
			),
		endTime: yup
			.object<moment.Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_EndTime"),
				})
			)
			.test(
				"test-EndTime",
				t("Common_Form_Error_InvalidTime"),
				function (value) {
					return moment(value).isValid();
				}
			),
		startDate: yup
			.object<moment.Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_StartDate"),
				})
			)
			.test(
				"test-StartDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			)
			.required(),
		endDate: yup
			.object<moment.Moment>()
			.typeError(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_EndDate"),
				})
			)
			.test(
				"test-EndDate",
				t("Common_Form_Error_InvalidDate"),
				function (value) {
					return moment(value).isValid();
				}
			)
			.required()
			.test(
				"test-endDateBeforeStartDate",
				t("Common_Form_Error_EndDateBeforeStartDate"),
				function (endDate: moment.Moment) {
					// This test can not be performed if start or end time are missing
					if (!(this.parent.endTime && this.parent.startTime)) {
						return true;
					}
					const endTime = getDateTime(endDate, this.parent.endTime);
					const startTime = getDateTime(
						this.parent.startDate,
						this.parent.startTime
					);
					if (!moment(this.parent.startDate).isValid()) {
						return true;
					}
					return endTime.isAfter(startTime);
				}
			)
			.test(
				"test-alreadyAssigned",
				t("ScheduleEntryForm_Error_AlreadyAssigned"),
				function (endDate: moment.Moment) {
					// This test can not be performed if start or end time are missing
					if (!(this.parent.endTime && this.parent.startTime)) {
						return true;
					}
					const userId = this.parent.userId as string;
					const resourceId = this.parent.resourceId as string;
					const typeId = this.parent.typeId as string;
					const kind = this.parent.kind as ScheduleEntryKind;
					const endTime = getDateTime(endDate, this.parent.endTime);
					const startTime = getDateTime(
						this.parent.startDate,
						this.parent.startTime
					);

					return !scheduleEntries.find(
						(scheduleEntry) =>
							(kind === ScheduleEntryKind.PROJECT
								? scheduleEntry.projectId
								: typeId === scheduleEntry.typeId) &&
							(userId === scheduleEntry.userId || !userId) &&
							(resourceId === scheduleEntry.resourceId || !resourceId) &&
							scheduleEntry.id !== props.scheduleEntry.id &&
							startTime.isBefore(moment.unix(scheduleEntry.endTime)) &&
							endTime.isAfter(moment.unix(scheduleEntry.startTime))
					);
				}
			),
		userId:
			scheduleEntryTypeCategory === ScheduleEntryTypeCategory.USER
				? yup.string().required(
						t("Common_Form_Error_MandatoryField", {
							field: t("ScheduleEntryForm_Label_User"),
						})
				  )
				: yup.string(),
		resourceId:
			scheduleEntryTypeCategory === ScheduleEntryTypeCategory.RESOURCE
				? yup.string().required(
						t("Common_Form_Error_MandatoryField", {
							field: t("ScheduleEntryForm_Label_Resource"),
						})
				  )
				: yup.string(),
		projectId: yup.string().when("kind", {
			is: ScheduleEntryKind.PROJECT,
			then: yup.string().required(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_Project"),
				})
			),
			otherwise: yup.string(),
		}),
		typeId: yup.string().when("kind", {
			is: ScheduleEntryKind.TYPE,
			then: yup.string().required(
				t("Common_Form_Error_MandatoryField", {
					field: t("ScheduleEntryForm_Label_Type"),
				})
			),
			otherwise: yup.string(),
		}),
	});

	const form = useForm<FormValues>({ validationSchema, defaultValues });
	const { control } = form;
	const values = form.getValues();
	const isDisabled =
		form.formState.isSubmitting || !isInternalAdmin || isDeleting;

	form.watch(["kind", "projectId"]);

	const redirectToProject = useRedirectToProject(projects);
	const isProjectRedirectable = useIsProjectRedirectable(
		values.projectId,
		projects
	);

	const previousProjectIdRef = useRef(props.scheduleEntry.projectId);

	useEffect(() => {
		if (values.projectId !== previousProjectIdRef.current) {
			const nextProjectNoteContent =
				projects.find((project) => project.id === values.projectId)
					?.noteContent || "";

			form.setValue("projectNoteContent", nextProjectNoteContent);
			previousProjectIdRef.current = values.projectId;
		}
	}, [form, projects, values.projectId]);

	const onSubmit = async (formValues: FormValues) => {
		const startMinutes = getMinutesFromMoment(formValues.startTime);
		const endMinutes = getMinutesFromMoment(formValues.endTime);
		if (
			startMinutes < visibleMinutes.start ||
			endMinutes > visibleMinutes.end
		) {
			changeVisibleHoursMC.open(formValues);
		} else {
			await submit(formValues);
		}
	};

	async function submit(formValues: FormValues) {
		try {
			const nextScheduleEntry: ScheduleEntry = {
				id: props.scheduleEntry.id,
				creationDate: moment().unix(),
				startTime: getDateTime(
					formValues.startDate,
					formValues.startTime
				).unix(),
				endTime: getDateTime(formValues.endDate, formValues.endTime).unix(),
				userId:
					scheduleEntryTypeCategory === ScheduleEntryTypeCategory.USER
						? formValues.userId
						: undefined,
				resourceId:
					scheduleEntryTypeCategory === ScheduleEntryTypeCategory.RESOURCE
						? formValues.resourceId
						: undefined,
				typeId:
					formValues.kind === ScheduleEntryKind.TYPE
						? formValues.typeId
						: undefined,
				projectId:
					formValues.kind === ScheduleEntryKind.PROJECT
						? formValues.projectId
						: undefined,
			};

			await useScheduleEntries.getState().setItem(nextScheduleEntry);

			if (formValues.projectId) {
				const project = projects.find(
					(project) => project.id === formValues.projectId
				);

				if ((project?.noteContent || "") !== formValues.projectNoteContent) {
					await useProjects.getState().updateProject({
						id: formValues.projectId,
						noteContent: formValues.projectNoteContent || undefined,
					});
				}
			}

			props.close();
		} catch (error) {
			snackbar.showMessage(verifyError(error).message);
		}
	}

	function renderMemberMenuItems() {
		if (companyMembers) {
			return getValidMembers(companyMembers).map((member) => {
				return (
					<MenuItem key={member.id} value={member.id}>
						{member.name} {member.lastname}
					</MenuItem>
				);
			});
		}
	}

	function renderResourceMenuItems() {
		return scheduleResources.map((scheduleResource) => (
			<MenuItem key={scheduleResource.id} value={scheduleResource.id}>
				{scheduleResource.name}
			</MenuItem>
		));
	}

	function renderScheduleEntryTypeMenuItems() {
		return scheduleEntryTypes.map((type, index) => {
			if (type.category === scheduleEntryTypeCategory && !type.isDeleted) {
				return (
					<MenuItem key={index} value={type.id}>
						<Box
							className={classes.selectMenuItemIcon}
							color={getColorHex(type.colorTag)}
						>
							{getScheduleEntryTypeIcon(type.icon, px.xl)}
						</Box>
						{translateScheduleEntryType(type)}
					</MenuItem>
				);
			} else {
				return null;
			}
		});
	}

	return (
		<Fragment>
			<ModalTitle
				close={props.close}
				isCloseAutoFocusEnabled
				title={
					isBrowserOrIpadOs
						? props.isNew
							? t("ScheduleEntryForm_Title_Create")
							: t("ScheduleEntryForm_Title_Edit")
						: ""
				}
			>
				{!props.isNew && (
					<Button
						disabled={isDisabled}
						onClick={props.disableEdit}
						variant="contained"
					>
						<VisibilityIcon />
					</Button>
				)}
				{isInternalAdmin && (
					<Box ml={px.s}>
						<Button
							disabled={isDisabled}
							onClick={form.handleSubmit(onSubmit)}
							variant="contained"
							color="primary"
							type="submit"
						>
							{props.isNew
								? t("Common_Dialog_Create")
								: t("Common_Dialog_Save")}
						</Button>
					</Box>
				)}
			</ModalTitle>
			<DialogContent>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<DatePickerField
							name="startDate"
							label={t("ScheduleEntryForm_Label_StartDate")}
							control={control}
							disabled={isDisabled}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TimePickerField
							name="startTime"
							label={t("ScheduleEntryForm_Label_StartTime")}
							control={control}
							disabled={isDisabled}
						/>
					</Grid>
				</Grid>
				<Grid container spacing={3}>
					<Grid item xs={12} sm={6}>
						<DatePickerField
							name="endDate"
							label={t("ScheduleEntryForm_Label_EndDate")}
							control={control}
							disabled={isDisabled}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TimePickerField
							name="endTime"
							label={t("ScheduleEntryForm_Label_EndTime")}
							control={control}
							disabled={isDisabled}
						/>
					</Grid>
				</Grid>
				<Box mt={3}>
					{scheduleEntryTypeCategory === ScheduleEntryTypeCategory.USER && (
						<SelectField
							name="userId"
							label={t("ScheduleEntryForm_Label_User")}
							control={control}
							disabled={isDisabled}
						>
							{renderMemberMenuItems()}
						</SelectField>
					)}
					{scheduleEntryTypeCategory === ScheduleEntryTypeCategory.RESOURCE && (
						<SelectField
							name="resourceId"
							label={t("ScheduleEntryForm_Label_Resource")}
							control={control}
							disabled={isDisabled}
						>
							{renderResourceMenuItems()}
						</SelectField>
					)}
				</Box>
				<Box
					display={props.isNew ? "flex" : "none"}
					justifyContent="center"
					mt={3}
				>
					<Controller
						as={RadioGroup}
						control={control}
						name="kind"
						value={values.kind}
						row
					>
						{[ScheduleEntryKind.PROJECT, ScheduleEntryKind.TYPE].map((kind) => (
							<FormControlLabel
								key={kind}
								value={kind}
								disabled={isDisabled}
								control={<Radio color="primary" />}
								labelPlacement="end"
								label={
									kind === ScheduleEntryKind.PROJECT
										? t("ScheduleEntryForm_Label_Project")
										: t("ScheduleEntryForm_Label_Type")
								}
							/>
						))}
					</Controller>
				</Box>
				<Box mt={3}>
					{values.kind === ScheduleEntryKind.PROJECT && (
						<ProjectField
							name="projectId"
							label={t("ScheduleEntryForm_Label_Project")}
							control={control}
							disabled={isDisabled}
						/>
					)}
					{values.kind === ScheduleEntryKind.TYPE && (
						<SelectField
							name="typeId"
							label={t("ScheduleEntryForm_Label_Type")}
							control={control}
							disabled={isDisabled}
						>
							{renderScheduleEntryTypeMenuItems()}
						</SelectField>
					)}
				</Box>
				{!!values.projectId && (
					<Box mt={3}>
						<TextField
							name="projectNoteContent"
							label={t("ScheduleEntryForm_Label_ProjectNoteContent")}
							control={control}
							disabled={isDisabled}
							multiline
						/>
					</Box>
				)}
			</DialogContent>
			{(isDeleteButtonVisible || isProjectRedirectable) && (
				<ModalActions>
					{isDeleteButtonVisible && (
						<Button
							type="button"
							color="secondary"
							startIcon={<DeleteOutlineIcon />}
							disabled={isDisabled}
							onClick={async () => {
								setIsDeleting(true);
								try {
									await useScheduleEntries
										.getState()
										.deleteItem(props.scheduleEntry);

									props.close();
								} catch (error) {
									snackbar.showMessage(verifyError(error).message);
									setIsDeleting(false);
								}
							}}
						>
							{t("Common_Dialog_Delete")}
						</Button>
					)}
					{isProjectRedirectable && (
						<Button
							type="button"
							color="primary"
							startIcon={<OpenInNewIcon />}
							onClick={() => {
								if (values.projectId) {
									redirectToProject(values.projectId);
								}
							}}
						>
							{t("Common_Dialog_OpenProject")}
						</Button>
					)}
				</ModalActions>
			)}
			<TinyModal
				title={t("ScheduleEntryForm_ChangeVisibleHours_Title")}
				modalController={changeVisibleHoursMC}
				actionLabel={t("Common_Dialog_Save")}
				action={async (formValues) => {
					if (formValues) {
						await submit(formValues);
					} else {
						snackbar.showMessage(
							"Can not find `formValues`. Please contact our support!"
						);
					}
				}}
			>
				<Box mb={px.xl}>
					{t("ScheduleEntryForm_ChangeVisibleHours_Message1")}
				</Box>
				<Box>{t("ScheduleEntryForm_ChangeVisibleHours_Message2")}</Box>
			</TinyModal>
		</Fragment>
	);
};

export default ScheduleEntryForm;

function getDateTime(date: moment.Moment, time: moment.Moment) {
	return moment(date).set({
		hour: time.get("hour"),
		minute: time.get("minute"),
	});
}

const useStyles = makeStyles({
	selectMenuItemIcon: {
		marginRight: px.m,
	},
});
